import React, { useEffect, useRef, useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import type { CustomEvalSetVersionResponse } from '@/lib/api/customEvals';

import {
  MAX_CRITERIA,
  NAME_MAX,
  type EditableCriterion,
  type EditState,
} from './CustomEvalDetailDialog.model';

interface VersionBodyProps {
  version: CustomEvalSetVersionResponse;
  state: EditState;
  isDraft: boolean;
  isBusy: boolean;
  onUpdateCriterion: (idx: number, patch: Partial<EditableCriterion>) => void;
  onAddCriterion: () => void;
  onRemoveCriterion: (idx: number) => void;
  onUpdateNotes: (notes: string) => void;
}

export function VersionBody({
  version,
  state,
  isDraft,
  isBusy,
  onUpdateCriterion,
  onAddCriterion,
  onRemoveCriterion,
  onUpdateNotes,
}: VersionBodyProps) {
  const disabled = !isDraft || isBusy;
  const atMaxCriteria = state.criteria.length >= MAX_CRITERIA;

  const [activeCriterionKey, setActiveCriterionKey] = useState<string | null>(
    () => state.criteria[0]?.key ?? null,
  );
  const prevCountRef = useRef(state.criteria.length);

  // Re-anchor when the version swaps OR the active key disappears (e.g. on
  // remove). Snap to the first criterion's key.
  useEffect(() => {
    if (state.criteria.length === 0) {
      if (activeCriterionKey !== null) setActiveCriterionKey(null);
      return;
    }
    const stillPresent =
      activeCriterionKey != null &&
      state.criteria.some((c) => c.key === activeCriterionKey);
    if (!stillPresent) {
      setActiveCriterionKey(state.criteria[0].key);
    }
  }, [version.id, state.criteria, activeCriterionKey]);

  // When a criterion is added (count grew), focus the newly-added tab (last).
  useEffect(() => {
    if (state.criteria.length > prevCountRef.current) {
      const last = state.criteria[state.criteria.length - 1];
      if (last) setActiveCriterionKey(last.key);
    }
    prevCountRef.current = state.criteria.length;
  }, [state.criteria]);

  return (
    <div className="space-y-4">
      <div className="space-y-1.5">
        <Label htmlFor={`ced-notes-${version.id}`} className="text-xs uppercase tracking-wide text-muted-foreground">
          Notes
        </Label>
        <Input
          id={`ced-notes-${version.id}`}
          value={state.notes}
          onChange={(e) => onUpdateNotes(e.target.value)}
          disabled={disabled}
          placeholder={isDraft ? 'Optional version notes' : 'No notes'}
        />
      </div>

      <div className="space-y-3">
        <h4 className="text-sm font-medium">
          Criteria ({state.criteria.length}/{MAX_CRITERIA})
        </h4>

        <Tabs
          value={activeCriterionKey ?? undefined}
          onValueChange={setActiveCriterionKey}
        >
          <TabsList className="flex h-auto flex-wrap gap-1">
            {state.criteria.map((c, idx) => (
              <TabsTrigger key={c.key} value={c.key} className="max-w-[160px] text-xs">
                <span className="truncate">
                  {c.name.trim() || `Criterion ${idx + 1}`}
                </span>
              </TabsTrigger>
            ))}
            {isDraft && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-7 px-2"
                onClick={onAddCriterion}
                disabled={disabled || atMaxCriteria}
                title={atMaxCriteria ? `Max ${MAX_CRITERIA} criteria` : 'Add criterion'}
                aria-label="Add criterion"
              >
                <Plus className="h-3.5 w-3.5" />
              </Button>
            )}
          </TabsList>

          {state.criteria.map((c, idx) => (
            <TabsContent key={c.key} value={c.key} className="mt-3">
              <CriterionEditor
                index={idx}
                criterion={c}
                disabled={disabled}
                isDraft={isDraft}
                canRemove={isDraft && state.criteria.length > 1}
                onChange={(patch) => onUpdateCriterion(idx, patch)}
                onRemove={() => onRemoveCriterion(idx)}
              />
            </TabsContent>
          ))}
        </Tabs>
      </div>
    </div>
  );
}

interface CriterionEditorProps {
  index: number;
  criterion: EditableCriterion;
  disabled: boolean;
  isDraft: boolean;
  canRemove: boolean;
  onChange: (patch: Partial<EditableCriterion>) => void;
  onRemove: () => void;
}

function CriterionEditor({
  index,
  criterion,
  disabled,
  isDraft,
  canRemove,
  onChange,
  onRemove,
}: CriterionEditorProps) {
  const [showContext, setShowContext] = useState(false);

  return (
    <div className="space-y-3 rounded-lg border bg-background p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1 space-y-1.5">
          <Label htmlFor={`ced-name-${index}`} className="text-xs uppercase tracking-wide text-muted-foreground">
            Name
          </Label>
          <Input
            id={`ced-name-${index}`}
            value={criterion.name}
            onChange={(e) => onChange({ name: e.target.value })}
            disabled={disabled}
            maxLength={NAME_MAX}
          />
          <p className="font-mono text-[10px] text-muted-foreground">
            key: {criterion.key}
          </p>
        </div>
        {canRemove && (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-8 w-8 text-muted-foreground hover:text-destructive"
            onClick={onRemove}
            disabled={disabled}
            title="Remove criterion"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        )}
      </div>

      <div className="space-y-1.5">
        <Label htmlFor={`ced-rubric-${index}`} className="text-xs uppercase tracking-wide text-muted-foreground">
          Scoring instructions
        </Label>
        <Textarea
          id={`ced-rubric-${index}`}
          value={criterion.rubric}
          onChange={(e) => onChange({ rubric: e.target.value })}
          disabled={disabled}
          rows={isDraft ? 14 : 10}
          className="font-mono text-xs"
        />
      </div>

      {criterion.metadata && Object.keys(criterion.metadata).length > 0 && (
        <div className="rounded-md border bg-muted/20 px-3 py-2">
          <button
            type="button"
            className="flex w-full items-center justify-between text-left text-xs font-medium"
            onClick={() => setShowContext((v) => !v)}
          >
            <span>Generation context</span>
            <span className="text-muted-foreground">{showContext ? 'Hide' : 'Show'}</span>
          </button>
          {showContext && (
            <pre className="mt-2 max-h-48 overflow-auto whitespace-pre-wrap break-words text-[11px] text-muted-foreground">
              {JSON.stringify(criterion.metadata, null, 2)}
            </pre>
          )}
        </div>
      )}
    </div>
  );
}
